import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';

interface AvailabilityHistoryPoint {
  timestamp: string;
  availabilityPercent: number;
  platform?: string;
  environment?: string;
}

interface PlatformAvailabilityResponse {
  environment: string;
  period: string;
  month: string;
  periodStart: string;
  periodEnd: string;
  measuredAt: string;
  availabilityPercent: number;
  targetPercent: number;
  meetingTarget: boolean;
  history: AvailabilityHistoryPoint[];
}

@Component({
  selector: 'app-system-monitoring',
  templateUrl: './system-monitoring.component.html',
  styleUrls: ['./system-monitoring.component.css'],
  standalone: false
})
export class SystemMonitoringComponent implements OnInit {
  selectedMonth = this.currentMonth();
  availability?: PlatformAvailabilityResponse;
  loading = false;
  error = '';
  chartOptions: any = {};

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAvailability();
    this.translate.onLangChange.subscribe(() => this.updateChart());
  }

  get displayedAvailability(): number | undefined {
    return this.availability?.availabilityPercent;
  }

  get meetsSelectedTarget(): boolean {
    return (this.displayedAvailability || 0) >= (this.availability?.targetPercent || 0);
  }

  get periodLabel(): string {
    const [year, month] = this.selectedMonth.split('-').map(Number);
    if (!year || !month) return this.selectedMonth;
    return new Intl.DateTimeFormat(this.translate.currentLang() || 'en', { month: 'long', year: 'numeric' })
      .format(new Date(year, month - 1, 1));
  }

  loadAvailability(): void {
    this.loading = true;
    this.error = '';
    const params = new HttpParams().set('month', this.selectedMonth);
    this.http.get<PlatformAvailabilityResponse>(this.endpoint, { params }).subscribe({
      next: availability => {
        this.availability = availability;
        this.updateChart();
        this.loading = false;
      },
      error: () => {
        this.availability = undefined;
        this.chartOptions = {};
        this.error = this.t('systemMonitoring.loadError', 'Die Verfügbarkeitsdaten konnten nicht geladen werden.');
        this.loading = false;
      }
    });
  }

  returnToPlatform(): void {
    this.router.navigate(['/home']);
  }

  updateChart(): void {
    if (!this.availability) {
      this.chartOptions = {};
      return;
    }

    const availabilityPercent = this.availability.availabilityPercent;
    const targetPercent = this.availability.targetPercent;
    const scaleMin = Math.max(0, Math.floor(Math.min(availabilityPercent, targetPercent) - 2));
    const valueColor = this.meetsSelectedTarget ? '#1b8f5a' : '#f05b3d';
    this.chartOptions = {
      background: { fill: 'transparent' },
      series: [
        {
          type: 'linear-gauge',
          direction: 'horizontal',
          value: availabilityPercent,
          cornerRadius: 8,
          scale: {
            min: scaleMin,
            max: 100,
            interval: { step: 1 }
          },
          bar: { fill: valueColor },
          label: {
            text: this.formatPercent(availabilityPercent),
            color: '#ffffff',
            fontWeight: '700',
            placement: 'inside-center'
          },
          targets: [{
            value: targetPercent,
            text: `${this.t('systemMonitoring.target', 'Zielwert')}: ${this.formatPercent(targetPercent)}`,
            shape: 'line',
            size: 18,
            stroke: '#334155',
            strokeWidth: 2,
            label: { enabled: true, color: '#334155', fontWeight: '600', spacing: 8 }
          }]
        },
      ]
    };
  }

  printPdfReport(): void {
    if (!this.availability) return;

    const report = window.open('', '_blank');
    if (!report) return;

    const title = this.t('systemMonitoring.title', 'Plattform-Verfügbarkeit');
    const period = this.periodLabel;
    const generatedAt = this.dateTimeLabel(new Date());
    const measuredAt = this.dateTimeLabel(new Date(this.availability.measuredAt));
    const status = this.meetsSelectedTarget
      ? this.t('systemMonitoring.targetMet', 'Ziel erreicht')
      : this.t('systemMonitoring.targetMissed', 'Ziel verfehlt');

    report.document.write(`<!doctype html>
      <html><head><meta charset="utf-8"><title>${this.escapeHtml(title)}</title>
      <style>
        body { color: #243347; font-family: Arial, sans-serif; margin: 48px; }
        h1 { color: #061d39; font-size: 26px; margin: 0 0 8px; }
        p { color: #667085; margin: 0 0 28px; }
        .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        .item { border: 1px solid #dce4ed; border-radius: 8px; padding: 16px; }
        .label { color: #667085; font-size: 12px; font-weight: bold; text-transform: uppercase; }
        .value { color: #061d39; font-size: 23px; font-weight: bold; margin-top: 8px; }
        .footer { border-top: 1px solid #dce4ed; color: #667085; font-size: 12px; margin-top: 34px; padding-top: 14px; }
      </style></head><body>
        <h1>${this.escapeHtml(title)}</h1>
        <p>${this.escapeHtml(period)}</p>
        <div class="grid">
          <div class="item"><div class="label">${this.escapeHtml(this.t('systemMonitoring.availability', 'Verfügbarkeit'))}</div><div class="value">${this.escapeHtml(this.formatPercent(this.displayedAvailability))}</div></div>
          <div class="item"><div class="label">${this.escapeHtml(this.t('systemMonitoring.target', 'Zielwert'))}</div><div class="value">${this.escapeHtml(this.formatPercent(this.availability.targetPercent))}</div></div>
          <div class="item"><div class="label">${this.escapeHtml(this.t('systemMonitoring.status', 'Status'))}</div><div class="value">${this.escapeHtml(status)}</div></div>
          <div class="item"><div class="label">${this.escapeHtml(this.t('systemMonitoring.measuredAt', 'Gemessen'))}</div><div class="value">${this.escapeHtml(measuredAt)}</div></div>
        </div>
        <div class="footer">${this.escapeHtml(this.t('systemMonitoring.createdAt', 'Bericht erstellt'))}: ${this.escapeHtml(generatedAt)}</div>
      </body></html>`);
    report.document.close();
    report.focus();
    window.setTimeout(() => report.print(), 250);
  }

  formatPercent(value: number | undefined): string {
    if (typeof value !== 'number') return '—';
    return new Intl.NumberFormat(this.translate.currentLang() || 'en', { minimumFractionDigits: 1, maximumFractionDigits: 3 }).format(value) + '%';
  }

  get firstMonthOfCurrentYear(): string {
    return `${new Date().getFullYear()}-01`;
  }

  get availableMonths(): string[] {
    const now = new Date();
    return Array.from(
      { length: now.getMonth() + 1 },
      (_, month) => `${now.getFullYear()}-${String(month + 1).padStart(2, '0')}`
    ).reverse();
  }

  monthLabel(month: string): string {
    const [year, monthNumber] = month.split('-').map(Number);
    return new Intl.DateTimeFormat(this.translate.currentLang() || 'en', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(year, monthNumber - 1, 1));
  }

  private get endpoint(): string {
    const baseUrl = environment.useEmulators
      ? 'http://localhost:5000'
      : `https://${environment.urlHostName}`;
    return `${baseUrl}/v1/platform-availability`;
  }

  currentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  private dateTimeLabel(value: Date): string {
    return new Intl.DateTimeFormat(this.translate.currentLang() || 'en', { dateStyle: 'medium', timeStyle: 'short' }).format(value);
  }

  private escapeHtml(value: string): string {
    const entities: Record<string, string> = {
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    };
    return value.replace(/[&<>"']/g, character => entities[character] ?? character);
  }

  t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value && value !== key ? value : fallback;
  }
}
